import { createContext, useCallback, useContext, useRef, useState } from "react";

const PageHeaderContext = createContext(null);

export const PageHeaderProvider = ({ children }) => {
  const [pageHeader, setPageHeaderState] = useState({
    title: null,
    updatedAt: null,
    source: null,
    entityId: null,
    hasRefresh: false,
    refreshing: false,
    refreshError: "",
  });

  // Store the refresh callback in a ref to avoid function-in-state issues.
  const refreshFnRef = useRef(null);

  const setPageHeader = useCallback(({
    title = null,
    updatedAt = null,
    source = null,
    entityId = null,
    onRefresh = null,
  } = {}) => {
    refreshFnRef.current = onRefresh || null;
    setPageHeaderState({
      title,
      updatedAt,
      source,
      entityId,
      hasRefresh: Boolean(onRefresh),
      refreshing: false,
      refreshError: "",
    });
  }, []);

  const clearPageHeader = useCallback(() => {
    refreshFnRef.current = null;
    setPageHeaderState({
      title: null,
      updatedAt: null,
      source: null,
      entityId: null,
      hasRefresh: false,
      refreshing: false,
      refreshError: "",
    });
  }, []);

  const triggerRefresh = useCallback(async () => {
    const fn = refreshFnRef.current;
    if (!fn) return;
    setPageHeaderState((prev) => ({ ...prev, refreshing: true }));
    try {
      await fn();
      setPageHeaderState((prev) => ({ ...prev, refreshError: "" }));
    } catch (error) {
      console.error("[page-header][frontend] refresh failed", error);
      setPageHeaderState((prev) => ({
        ...prev,
        refreshError: error?.message || "Refresh failed",
      }));
    } finally {
      setPageHeaderState((prev) => ({ ...prev, refreshing: false }));
    }
  }, []);

  return (
    <PageHeaderContext.Provider value={{ pageHeader, setPageHeader, clearPageHeader, triggerRefresh }}>
      {children}
    </PageHeaderContext.Provider>
  );
};

export const usePageHeader = () => {
  const ctx = useContext(PageHeaderContext);
  if (!ctx) throw new Error("usePageHeader must be used inside PageHeaderProvider");
  return ctx;
};
